import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Download, Trash2, ShieldAlert, ArrowLeft } from "lucide-react";
import api from "../../api/axios";
import useAuthStore from "../../store/auth.store";

// Privacy + DPDP Act 2023 compliance page.
//   - Right to Access  → Export-data button (downloads JSON dump)
//   - Right to Erasure → Delete account (soft-delete + PII anonymization)
//
// Both actions require the user to confirm with their current password.
// Account deletion is irreversible — the user can never log back in
// (status=BLOCKED, password unset, email/phone wiped).
export default function Privacy() {
  const navigate = useNavigate();
  const { logout } = useAuthStore();
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");

  const [delConfirm, setDelConfirm] = useState(false);
  const [delPassword, setDelPassword] = useState("");
  const [delBusy, setDelBusy] = useState(false);
  const [delError, setDelError] = useState("");

  const handleExport = async () => {
    setExporting(true); setExportError("");
    try {
      const r = await api.post("/api/accounts/export-data/", null, {
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `zencricketdata_export.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setExportError(e?.response?.data?.detail || e.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async () => {
    if (!delPassword) { setDelError("Password required."); return; }
    setDelBusy(true); setDelError("");
    try {
      await api.post("/api/accounts/delete-account/", { password: delPassword });
      // Log out + redirect home — user record is soft-deleted server-side.
      try { await logout?.(); } catch { /* ignore */ }
      navigate("/");
    } catch (e) {
      setDelError(e?.response?.data?.detail || e.message || "Delete failed");
    } finally {
      setDelBusy(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-6">
      {/* 2026-06-08 — Back affordance per nav audit. */}
      <Link
        to="/dashboard"
        className="inline-flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary"
        aria-label="Back to dashboard"
      >
        <ArrowLeft className="w-4 h-4" /> Back to dashboard
      </Link>
      <h1 className="text-2xl font-bold text-text-primary">Privacy &amp; data</h1>
      <p className="text-sm text-text-secondary">
        Under India's Digital Personal Data Protection Act (DPDP Act 2023),
        you have the right to access your personal data and to delete your
        account. Both controls live below.
      </p>

      {/* Right to Access */}
      <div className="rounded-xl border border-border-default bg-bg-primary p-5 space-y-3">
        <div className="flex items-start gap-3">
          <Download className="w-5 h-5 text-button-primary mt-1 shrink-0" />
          <div className="flex-1">
            <h2 className="text-base font-semibold text-text-primary">
              Download your data
            </h2>
            <p className="text-sm text-text-secondary mt-1">
              Get a JSON file with your account profile, subscriptions, wallet
              transactions, and the matches you own.
            </p>
          </div>
        </div>
        <button
          onClick={handleExport}
          disabled={exporting}
          className="text-sm px-4 py-2 rounded-lg bg-button-primary text-white disabled:opacity-50"
        >
          {exporting ? "Preparing…" : "Download JSON"}
        </button>
        {exportError && (
          <div className="text-sm text-rose-400">⚠ {exportError}</div>
        )}
      </div>

      {/* Right to Erasure */}
      <div className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-5 space-y-3">
        <div className="flex items-start gap-3">
          <ShieldAlert className="w-5 h-5 text-rose-400 mt-1 shrink-0" />
          <div className="flex-1">
            <h2 className="text-base font-semibold text-rose-300">
              Delete account
            </h2>
            <p className="text-sm text-text-secondary mt-1">
              Your email, phone, and name will be removed and you will be
              permanently locked out. Match data you've recorded stays in
              the system (anonymized) so co-scorers and historical analytics
              are not broken.{" "}
              <strong className="text-rose-300">This cannot be undone.</strong>
            </p>
          </div>
        </div>
        {!delConfirm ? (
          <button
            onClick={() => setDelConfirm(true)}
            className="text-sm px-4 py-2 rounded-lg border border-rose-500/40 text-rose-300 hover:bg-rose-500/10 inline-flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4" /> Delete my account
          </button>
        ) : (
          <div className="space-y-3">
            <label className="block">
              <span className="text-xs uppercase tracking-wider text-text-secondary">
                Confirm with your current password
              </span>
              <input
                type="password"
                value={delPassword}
                onChange={(e) => setDelPassword(e.target.value)}
                className="mt-1 w-full bg-bg-secondary border border-border-default rounded px-3 py-2 text-sm"
                autoComplete="current-password"
              />
            </label>
            {delError && (
              <div className="text-sm text-rose-400">⚠ {delError}</div>
            )}
            <div className="flex gap-2">
              <button
                onClick={handleDelete}
                disabled={delBusy || !delPassword}
                className="text-sm px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-500 text-white disabled:opacity-50"
              >
                {delBusy ? "Deleting…" : "Permanently delete"}
              </button>
              <button
                onClick={() => { setDelConfirm(false); setDelPassword(""); setDelError(""); }}
                disabled={delBusy}
                className="text-sm px-4 py-2 rounded-lg bg-bg-secondary text-text-primary"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
